import React, { useMemo, useState } from "react";

import { i18n } from "../../translate/i18n";
import { normalizeTicketCountBadge } from "../../helpers/ticketCountBadge";
import { getTailwickChatsHeaderTitle } from "./ChatListSection";
import RecentTicketSearches from "../../components/RecentTicketSearches";
import ChatTicketsFilterRail from "./ChatTicketsFilterRail";
import ChatLayoutToggle from "../../components/ChatLayout/ChatLayoutToggle";

function SearchIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="size-3.5 text-default-500"
			aria-hidden
		>
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.3-4.3" />
		</svg>
	);
}

function QuickFilterPill({ active, onClick, children, badge, ariaLabel }) {
	const { show, label } = normalizeTicketCountBadge(badge);

	return (
		<button
			type="button"
			className={`relative rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
				active
					? "bg-primary/15 text-primary"
					: "border border-default-200 text-default-600 hover:bg-default-150"
			}`}
			onClick={onClick}
			aria-label={ariaLabel}
		>
			{children}
			{show && (
				<span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
					{label}
				</span>
			)}
		</button>
	);
}

/**
 * Cabeçalho da coluna Chats — markup Tailwick Chats.tsx.
 */
export default function TailwickChatsHeader({
	tab = "open",
	showUnread,
	setShowUnread,
	searchParam,
	onSearchChange,
	onSearchEnter,
	searchLoading,
	searched,
	onNewTicket,
	openUnreadMessagesSum,
	setBulkSelection,
	bulkSelection,
	recentSearches = [],
	onSelectRecent,
	onRemoveRecent,
	onClearRecents,
	darkMode = false,
	filterRailProps = null,
}) {
	const headerTitle = getTailwickChatsHeaderTitle(tab);
	const [open, setOpen] = useState(false);
	const [searchFocused, setSearchFocused] = useState(false);
	const showRecents = searchFocused && !String(searchParam || "").trim();

	const hasAdvancedFilters = useMemo(() => {
		if (!filterRailProps) return false;
		const {
			user,
			selectedQueueIds = [],
			selectedTagIds = [],
			selectedChannels = [],
			filterModalOpen,
		} = filterRailProps;
		const defaultQueueIds = (user?.queues || []).map((queue) => queue.id);
		const hasQueueFilterActive =
			selectedQueueIds.length > 0 &&
			(selectedQueueIds.length !== defaultQueueIds.length ||
				!defaultQueueIds.every((id) => selectedQueueIds.includes(id)));
		return (
			hasQueueFilterActive ||
			selectedTagIds.length > 0 ||
			selectedChannels.length > 0 ||
			!!filterModalOpen
		);
	}, [filterRailProps]);

	const closeMenu = () => setOpen(false);

	return (
		<div className="tailwick-chats-header card-body shrink-0 border-b border-default-200 pb-4">
			<div className="tailwick-chats-header__top">
				<h6 className="tailwick-chats-header__title card-title m-0 truncate">{headerTitle}</h6>
				{filterRailProps ? (
					<div className="tailwick-chats-header__status">
						<ChatTicketsFilterRail {...filterRailProps} variant="inline" />
					</div>
				) : null}
			</div>
			<div className="tailwick-chats-header__search-row relative mt-5">
				<div className="relative min-w-0 flex-1">
					<input
						type="search"
						className="form-input form-input-sm block w-full ps-11"
						placeholder={i18n.t("chat.layout.searchPlaceholder")}
						value={searchParam}
						autoComplete="off"
						onChange={onSearchChange}
						onFocus={() => setSearchFocused(true)}
						onBlur={() => setSearchFocused(false)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								onSearchEnter(e.target.value);
							}
						}}
						style={{ paddingRight: searchLoading && searched > 0 ? "2.5rem" : undefined }}
					/>
					<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-4">
						<SearchIcon />
					</div>
					{searchLoading && searched > 0 && (
						<span className="absolute inset-y-0 end-3 flex items-center">
							<span
								className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
								role="status"
								aria-label="Buscando"
							/>
						</span>
					)}
					<RecentTicketSearches
						open={showRecents}
						items={recentSearches}
						dark={darkMode}
						onSelect={onSelectRecent}
						onRemove={onRemoveRecent}
						onClear={onClearRecents}
					/>
				</div>

				<div className="tailwick-chats-header__menu-wrap">
					<button
						className={`tailwick-chats-header-menu__toggle${
							hasAdvancedFilters ? " tailwick-chats-header-menu__toggle--active" : ""
						}`}
						type="button"
						onClick={() => setOpen(!open)}
						aria-label="Mais opções"
						aria-expanded={open}
					>
						&#8942;
					</button>
					{open && (
						<div role="menu" className="tailwick-chats-header-menu">
							{filterRailProps ? (
								<button
									type="button"
									role="menuitem"
									className="tailwick-chats-header-menu__item"
									onClick={() => {
										filterRailProps.onFilterModalOpen();
										closeMenu();
									}}
								>
									{i18n.t("chat.layout.filters")}
									{hasAdvancedFilters ? " •" : ""}
								</button>
							) : null}

							<button
								type="button"
								role="menuitem"
								className="tailwick-chats-header-menu__item"
								onClick={() => {
									onNewTicket();
									closeMenu();
								}}
							>
								{i18n.t("chat.layout.newConversation")}
							</button>

							{filterRailProps ? (
								<ChatLayoutToggle asMenuItem onAfterClick={closeMenu} />
							) : null}

							<button
								type="button"
								role="menuitem"
								className="tailwick-chats-header-menu__item"
								onClick={() => {
									setBulkSelection(!bulkSelection);
									closeMenu();
								}}
							>
								{bulkSelection ? "Deselecionar tickets" : "Selecionar tickets"}
							</button>
						</div>
					)}
				</div>
			</div>
			{tab === "open" && (
				<div className="tailwick-quick-filters mt-3 mb-1 flex flex-wrap gap-2">
					<QuickFilterPill
						active={showUnread === false}
						onClick={() => setShowUnread(false)}
					>
						{i18n.t("chat.layout.filterAll")}
					</QuickFilterPill>
					<QuickFilterPill
						active={showUnread === true}
						onClick={() => setShowUnread(true)}
						badge={openUnreadMessagesSum}
						ariaLabel={
							openUnreadMessagesSum
								? i18n.t("chat.layout.filterUnreadWithCount", {
										count: openUnreadMessagesSum,
									})
								: i18n.t("chat.layout.filterUnread")
						}
					>
						{i18n.t("chat.layout.filterUnread")}
					</QuickFilterPill>
					<QuickFilterPill
						active={showUnread === "grupos"}
						onClick={() => setShowUnread("grupos")}
					>
						{i18n.t("chat.layout.filterGroups")}
					</QuickFilterPill>
				</div>
			)}
		</div>
	);
}
